import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { parseServerTimestamp } from "./traceUtils.js";

const DAY_MS = 86400 * 1000;
const WINDOW_MS = 30 * DAY_MS;
const BATCH_SIZE = 500;
export const TELEMETRY_PAGE_LIMIT = 1000;

export const TELEMETRY_EVENT_KINDS = {
  ProcessStart: "PROCESS_START", DnsQuery: "DNS_QUERY",
  FileState: "STATE", FileDelete: "DELETE", FileRename: "RENAME",
  NetworkConnection: "NETWORK_CONNECTION",
};
const LEGACY_KINDS = Object.fromEntries(Object.entries(TELEMETRY_EVENT_KINDS).map(([key, value]) => [value, key]));

export const telemetryField = (name, alias = "e") => ({
  stepAlias: alias, field: `DIMENSION_FIELD_TELEMETRY_TIMELINE_${name}`,
});

const FAMILIES = [
  { family: "process", entity: "PROCESS_EVENT", prefix: "PROCESS_EVENT", kinds: ["PROCESS_START"], fields: {
    IMAGE_NAME: "image_name", COMMAND_LINE: "command_line", ACCOUNT_NAME: "account_name", DOMAIN_NAME: "domain_name",
    PARENT_PROCESS_START_KEY: "parent_process_start_key", CREATOR_PROCESS_START_KEY: "creator_process_start_key",
  } },
  { family: "file", entity: "FILE_EVENT", prefix: "FILE_EVENT", kinds: ["STATE", "DELETE", "RENAME"], fields: {
    PRIMARY_PATH: "filepath", SOURCE_PATH: "original_filepath", DESTINATION_PATH: "new_filepath",
    FIRST_READ_TIME: "first_read_time", FIRST_WRITE_TIME: "first_write_time", KIND: "event_kind",
  } },
  { family: "dns", entity: "DNS_EVENT", prefix: "DNS_EVENT", kinds: ["DNS_QUERY"], fields: { QUERY_NAME: "query_name" } },
  { family: "network_connection", entity: "NETWORK_CONNECTION", prefix: "NETWORK_CONNECTION", kinds: ["NETWORK_CONNECTION"], fields: {
    REMOTE_ADDRESS: "remote_address", REMOTE_PORT: "remote_port", REMOTE_PORT_TEXT: "remote_port_text",
  } },
];

const DETAIL_FIELDS = {
  process: ["PROCESS_ID", "SESSION_ID", "PACKAGE_NAME", "RELATIVE_APP_NAME", "CREATE_TIME", "ATTRIBUTION_METHOD"],
  file: ["STATE_EVENT_ID", "UPDATED_FIELD", "OPEN_TIME", "CLOSE_TIME", "SIZE", "IS_PARTIAL", "ACTIONS"],
  dns: ["RECORD_TYPE", "RESPONSE_CODE", "RESOLVED_ADDRESSES", "SERVER", "TTL_SECONDS"],
  network_connection: ["BYTES_RECEIVED", "BYTES_SENT", "START", "END", "DURATION_MS"],
};

const pred = (field, op, value) => ({ pred: { field, op, value } });
const and = (children) => children.length === 1 ? children[0] : { andExpr: { children } };
const familyField = (family, name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_${family.prefix}_${name}` });
const rowKey = (row) => `${row.endpointId}:${row.id}:${row.family}`;

// Typed telemetry has a 30-day query-window limit. Preserve the requested
// range with sequential bounded windows, keeping at most one page per window.
function timeWindows(timeRange) {
  const end = timeRange?.absolute ? parseServerTimestamp(timeRange.absolute.end) : Date.now();
  const start = timeRange?.absolute ? parseServerTimestamp(timeRange.absolute.start)
    : end - Number(timeRange?.relative?.durationSeconds ?? 30 * 86400) * 1000;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  if (end - start > 90 * DAY_MS) throw new Error("Activity queries support at most 90 days at a time.");
  const windows = [];
  for (let from = start; from < end; from += WINDOW_MS) {
    windows.push({ absolute: { start: new Date(from).toISOString(), end: new Date(Math.min(end, from + WINDOW_MS)).toISOString() } });
  }
  return windows;
}

function sortRows(rows, orderBy) {
  return rows.sort((a, b) => {
    for (const { outputAlias, direction } of orderBy ?? []) {
      const av = a[outputAlias] ?? "";
      const bv = b[outputAlias] ?? "";
      const comparison = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      if (comparison) return direction === "DIRECTION_DESC" ? -comparison : comparison;
    }
    return rowKey(a).localeCompare(rowKey(b));
  });
}

function familyDimensions(family) {
  return [
    ["EVENT_ID", "id"], ["ENDPOINT_ID", "endpointId"], ["TRACE_ID", "sessionId"],
    ["EVENT_TIME", "ts"], ["PROCESS_START_KEY", "psk"], ...Object.entries(family.fields),
  ].map(([name, outputAlias]) => ({ field: familyField(family, name), outputAlias }));
}

function familyRow(row, family) {
  return {
    ...row, family: family.family,
    eventType: row.event_kind ?? family.kinds[0],
    data: Object.fromEntries(Object.values(family.fields).map((key) => [key, row[key]])),
  };
}

// Translate a timeline filter onto one family's columns: false when the family
// cannot match (none of its kinds selected). This adapter only handles the
// conjunctions constructed by these readers.
function familyFilter(filter, family) {
  if (!filter) return null;
  if (filter.andExpr) {
    const children = filter.andExpr.children.map((child) => familyFilter(child, family));
    if (children.includes(false)) return false;
    const kept = children.filter(Boolean);
    return kept.length ? and(kept) : null;
  }
  if (filter.orExpr) {
    const children = filter.orExpr.children.map((child) => familyFilter(child, family));
    if (children.includes(null)) return null;
    const kept = children.filter((child) => child !== false);
    return kept.length ? { orExpr: { children: kept } } : false;
  }
  const p = filter.pred;
  if (!p) throw new Error("Unsupported activity filter.");
  const name = p.field.field.replace("DIMENSION_FIELD_TELEMETRY_TIMELINE_", "");
  if (name === "EVENT_KIND") {
    if (!["OPERATOR_EQ", "OPERATOR_IN"].includes(p.op)) throw new Error("Unsupported activity-kind filter.");
    const values = (Array.isArray(p.value) ? p.value : [p.value]).filter((value) => family.kinds.includes(value));
    if (!values.length) return false;
    return family.family === "file" ? pred(familyField(family, "KIND"), "OPERATOR_IN", values) : null;
  }
  const column = { EVENT_ID: "EVENT_ID", ENDPOINT_ID: "ENDPOINT_ID", SESSION_ID: "TRACE_ID", EVENT_TIME: "EVENT_TIME", PROCESS_START_KEY: "PROCESS_START_KEY" }[name];
  if (!column) throw new Error("Unsupported activity field.");
  return { pred: { ...p, field: familyField(family, column) } };
}

// The family tables' own sort key leads with the endpoint, so an
// endpoint-scoped read prunes to that endpoint's granules. The timeline index is
// time-first: an endpoint filter there still pays for every granule the endpoint
// appears in, and a continuously active endpoint appears in nearly all of them.
// Route only filters every family can carry exactly. Without a kind filter the
// timeline would also return trace_activity rows, which have no family here.
const ROUTABLE_FIELDS = new Set(["EVENT_KIND", "EVENT_ID", "ENDPOINT_ID", "SESSION_ID", "EVENT_TIME", "PROCESS_START_KEY"]);
const FAMILY_KINDS = new Set(FAMILIES.flatMap((family) => family.kinds));
const ENDPOINT_ROUTE_LIMIT = 50;
const timelineName = (p) => p.field?.field?.replace("DIMENSION_FIELD_TELEMETRY_TIMELINE_", "");
const conjuncts = (filter) => filter?.andExpr ? filter.andExpr.children.flatMap(conjuncts) : filter ? [filter] : [];
function routableFields(filter) {
  if (filter.andExpr || filter.orExpr) return (filter.andExpr ?? filter.orExpr).children.every(routableFields);
  return Boolean(filter.pred) && ROUTABLE_FIELDS.has(timelineName(filter.pred));
}
function familyRouted(filter) {
  if (!filter || !routableFields(filter)) return false;
  const top = conjuncts(filter).map((child) => child.pred).filter(Boolean);
  const values = (p) => Array.isArray(p.value) ? p.value : [p.value];
  const endpoint = top.some((p) => timelineName(p) === "ENDPOINT_ID"
    && ["OPERATOR_EQ", "OPERATOR_IN"].includes(p.op)
    && values(p).length > 0 && values(p).length <= ENDPOINT_ROUTE_LIMIT && values(p).every(Boolean));
  const kinds = top.some((p) => timelineName(p) === "EVENT_KIND"
    && ["OPERATOR_EQ", "OPERATOR_IN"].includes(p.op) && values(p).every((kind) => FAMILY_KINDS.has(kind)));
  return endpoint && kinds;
}

const ENVELOPE_ALIASES = ["id", "endpointId", "sessionId", "ts", "psk", "event_kind"];
const envelopeDimensions = (family) => familyDimensions(family).filter((dimension) => ENVELOPE_ALIASES.includes(dimension.outputAlias));

// One bounded page per family, merged in the requested order. Each family's
// page is its own top rows under the same order, so the merged top `limit` is
// exact. `extraFilter` narrows a family further (search terms).
async function readFamilies(spec, execute, extraFilter = () => null) {
  const pages = await Promise.all(FAMILIES.map(async (family) => {
    const filter = familyFilter(spec.filter, family);
    if (filter === false) return [];
    const parts = [filter, extraFilter(family)].filter(Boolean);
    const orderBy = (spec.orderBy ?? []).filter((order) => !["eventType", "family"].includes(order.outputAlias));
    // The supported file kinds have enum order STATE, RENAME, DELETE.
    // Descending enum order matches the display order Delete, Rename, State.
    if (family.family === "file" && spec.orderBy?.some((order) => order.outputAlias === "eventType")) {
      orderBy.unshift({ outputAlias: "event_kind", direction: "DIRECTION_DESC" });
    }
    const result = rowsToObjects(await execute({
      root: { alias: "e", entity: `ENTITY_TYPE_${family.entity}` },
      timeRange: spec.timeRange, filter: parts.length ? and(parts) : undefined,
      output: { dimensions: envelopeDimensions(family) }, orderBy, limit: spec.limit,
    }));
    return result.map((row) => familyRow(row, family));
  }));
  return sortRows(pages.flat(), spec.orderBy).slice(0, spec.limit);
}

// Search the displayed family fields, including network ports that are absent
// from the timeline's indexed summary. This also preserves short searches.
function readFamilySearch(spec, search, execute) {
  const pattern = `%${search.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_")}%`;
  return readFamilies(spec, execute, (family) => {
    const textFields = Object.keys(family.fields).filter((name) => !name.endsWith("_KEY") && !["FIRST_READ_TIME", "FIRST_WRITE_TIME", "KIND", "REMOTE_PORT"].includes(name));
    return { orExpr: { children: textFields.map((name) => pred(familyField(family, name), "OPERATOR_ILIKE", pattern)) } };
  });
}

// Overview keyset pages over each family, k-way merged in (ts, id, endpoint,
// family) order. A family refills only once its buffered rows were all emitted,
// so each refill is bounded by the rows still needed.
const OVERVIEW_FAMILY_KEYS = [["EVENT_TIME", "ts"], ["EVENT_ID", "id"], ["ENDPOINT_ID", "endpointId"]];
const compareKeys = (a, b) => {
  for (const alias of ["ts", "id", "endpointId", "family"]) {
    const av = String(a[alias] ?? ""), bv = String(b[alias] ?? "");
    if (av !== bv) return av < bv ? -1 : 1;
  }
  return 0;
};
async function readFamilyOverview(spec, timeRange, need, execute) {
  const streams = FAMILIES.map((family) => ({ family, filter: familyFilter(spec.filter, family), buffer: [], cursor: null, done: false }))
    .filter((stream) => stream.filter !== false);
  const fill = async (stream, pageSize) => {
    const { family, cursor } = stream;
    const after = cursor && { orExpr: { children: OVERVIEW_FAMILY_KEYS.map(([field, alias], index) => and([
      ...OVERVIEW_FAMILY_KEYS.slice(0, index).map(([equalField, equalAlias]) => pred(familyField(family, equalField), "OPERATOR_EQ", cursor[equalAlias])),
      pred(familyField(family, field), "OPERATOR_GT", cursor[alias]),
    ])) } };
    const parts = [stream.filter, after].filter(Boolean);
    const page = rowsToObjects(await execute({
      root: { alias: "e", entity: `ENTITY_TYPE_${family.entity}` }, timeRange,
      filter: parts.length ? and(parts) : undefined, output: { dimensions: envelopeDimensions(family) },
      orderBy: OVERVIEW_FAMILY_KEYS.map(([, outputAlias]) => ({ outputAlias, direction: "DIRECTION_ASC" })), limit: pageSize,
    }));
    if (page.length < pageSize) stream.done = true;
    if (!page.length) return;
    const last = page[page.length - 1];
    if (cursor && OVERVIEW_FAMILY_KEYS.every(([, alias]) => last[alias] === cursor[alias])) throw new Error("Activity overview did not advance.");
    stream.cursor = last;
    stream.buffer = page.map(({ id, endpointId, sessionId, ts, psk, event_kind: kind }) => ({
      id, endpointId, sessionId, ts, psk, family: family.family, eventType: kind ?? family.kinds[0],
    }));
  };
  const rows = [];
  while (rows.length < need) {
    const pageSize = Math.min(TELEMETRY_PAGE_LIMIT, need - rows.length);
    await Promise.all(streams.filter((stream) => !stream.buffer.length && !stream.done).map((stream) => fill(stream, pageSize)));
    let next = null;
    for (const stream of streams) {
      if (stream.buffer.length && (!next || compareKeys(stream.buffer[0], next.buffer[0]) < 0)) next = stream;
    }
    if (!next) break;
    rows.push(next.buffer.shift());
  }
  return rows;
}

async function hydrate(rows, timeRange, execute) {
  const byKey = new Map();
  for (const family of FAMILIES) {
    const selected = rows.filter((row) => row.family === family.family);
    for (let start = 0; start < selected.length; start += BATCH_SIZE) {
      const batch = selected.slice(start, start + BATCH_SIZE);
      const result = rowsToObjects(await execute({
        root: { alias: "e", entity: `ENTITY_TYPE_${family.entity}` }, timeRange,
        filter: { orExpr: { children: batch.map((row) => and([
          pred(familyField(family, "EVENT_ID"), "OPERATOR_EQ", row.id),
          pred(familyField(family, "ENDPOINT_ID"), "OPERATOR_EQ", row.endpointId),
        ])) } },
        output: { dimensions: familyDimensions(family) },
        limit: BATCH_SIZE,
      }));
      for (const row of result) {
        const record = familyRow(row, family);
        byKey.set(rowKey(record), record.data);
      }
    }
  }
  return rows.map((row) => {
    const data = byKey.get(rowKey(row));
    if (!data) throw new Error("Some activity details could not be loaded. Try again.");
    return { ...row, data };
  });
}

export function telemetryDimensions() {
  return [
    ["EVENT_ID", "id"], ["ENDPOINT_ID", "endpointId"], ["SESSION_ID", "sessionId"],
    ["EVENT_TIME", "ts"], ["EVENT_KIND", "eventType"], ["FAMILY", "family"],
    ["PROCESS_START_KEY", "psk"],
  ].map(([name, outputAlias]) => ({ field: telemetryField(name), outputAlias }));
}

// The trace overview needs up to 20,000 timestamps for bucketing, but each
// typed query may return only 1,000 rows. Continue in database key order without
// offsets or payload reads. Preserve the cursor's full timestamp precision.
export async function readTelemetryOverview(spec, { execute = runQuery } = {}) {
  const limit = spec.limit ?? 500;
  if (!Number.isInteger(limit) || limit < 1 || limit > 20000) throw new Error("Activity overviews support between 1 and 20000 rows.");
  const keys = [["EVENT_TIME", "ts"], ["EVENT_ID", "id"], ["ENDPOINT_ID", "endpointId"], ["FAMILY", "family"]];
  const orderBy = keys.map(([, outputAlias]) => ({ outputAlias, direction: "DIRECTION_ASC" }));
  const rows = [];
  let cursor = null;
  const routed = familyRouted(spec.filter);
  for (const timeRange of timeWindows(spec.timeRange)) {
    if (routed) {
      if (rows.length < limit) rows.push(...await readFamilyOverview(spec, timeRange, limit - rows.length, execute));
      continue;
    }
    while (rows.length < limit) {
      const after = cursor && { orExpr: { children: keys.map(([field, alias], index) => and([
        ...keys.slice(0, index).map(([equalField, equalAlias]) => pred(telemetryField(equalField), "OPERATOR_EQ", cursor[equalAlias])),
        pred(telemetryField(field), "OPERATOR_GT", cursor[alias]),
      ])) } };
      const pageSize = Math.min(TELEMETRY_PAGE_LIMIT, limit - rows.length);
      const filters = [spec.filter, after].filter(Boolean);
      const page = rowsToObjects(await execute({
        ...spec, timeRange, output: { dimensions: telemetryDimensions() }, orderBy,
        filter: filters.length ? and(filters) : undefined, limit: pageSize,
      }));
      if (!page.length) break;
      const last = page[page.length - 1];
      if (cursor && keys.every(([, alias]) => last[alias] === cursor[alias])) throw new Error("Activity overview did not advance.");
      cursor = last;
      rows.push(...page);
      if (page.length < pageSize) break;
    }
  }
  return rows.map((row) => ({ ...row, eventType: LEGACY_KINDS[row.eventType] ?? row.eventType }));
}

export async function readTelemetryPage(spec, { search = "", details = true, execute = runQuery } = {}) {
  const limit = spec.limit ?? TELEMETRY_PAGE_LIMIT;
  if (limit < 1 || limit > TELEMETRY_PAGE_LIMIT) throw new Error(`Activity pages support between 1 and ${TELEMETRY_PAGE_LIMIT} rows.`);
  const term = String(search).trim();
  const windows = timeWindows(spec.timeRange);
  const routed = familyRouted(spec.filter);
  let selected = [];
  for (const [windowIndex, timeRange] of windows.entries()) {
    const windowSpec = { ...spec, timeRange, limit, output: { dimensions: telemetryDimensions() } };
    const rows = term ? await readFamilySearch(windowSpec, term, execute)
      : routed ? await readFamilies(windowSpec, execute)
      : rowsToObjects(await execute(windowSpec));
    const candidates = new Map(selected.map((row) => [rowKey(row), row]));
    for (const row of rows) {
      if (Number.isFinite(parseServerTimestamp(row.ts))) candidates.set(rowKey(row), { ...row, windowIndex });
    }
    selected = sortRows([...candidates.values()], spec.orderBy).slice(0, limit);
  }
  if (details) {
    const hydrated = new Map();
    for (const [windowIndex, timeRange] of windows.entries()) {
      const rows = await hydrate(selected.filter((row) => row.windowIndex === windowIndex), timeRange, execute);
      for (const row of rows) hydrated.set(rowKey(row), row);
    }
    selected = selected.map((row) => hydrated.get(rowKey(row)) ?? row);
  }
  return selected.map(({ windowIndex: _windowIndex, ...row }) => ({
    ...row, eventType: LEGACY_KINDS[row.eventType] ?? row.eventType,
  }));
}

// Discover only envelopes across the retention window, then read one typed
// family with its endpoint-first key. A shared event ID must not pick an
// arbitrary endpoint when an old deep link contains only the event ID.
export async function readTelemetryEvent({ id, endpointId, timestamp } = {}, { execute = runQuery } = {}) {
  if (!id) return null;
  const ts = typeof timestamp === "number" ? timestamp : parseServerTimestamp(timestamp);
  const timeRange = Number.isFinite(ts) && ts > 0
    ? { absolute: { start: new Date(ts - 1000).toISOString(), end: new Date(ts + 1000).toISOString() } }
    : { relative: { durationSeconds: String(90 * 86400) } };
  const filters = [pred(telemetryField("EVENT_ID"), "OPERATOR_EQ", id)];
  if (endpointId) filters.push(pred(telemetryField("ENDPOINT_ID"), "OPERATOR_EQ", endpointId));
  const rows = await readTelemetryPage({
    root: { alias: "e", entity: "ENTITY_TYPE_TELEMETRY_TIMELINE" },
    timeRange, filter: and(filters), limit: 2,
  }, { details: false, execute });
  if (!rows.length) return null;
  if (rows.length !== 1) throw new Error("Open this activity from its endpoint to see its details.");
  const row = rows[0];
  const family = FAMILIES.find((candidate) => candidate.family === row.family);
  if (!family) throw new Error("Details are not available for this activity type.");
  const eventTime = parseServerTimestamp(row.ts);
  const fields = {
    ...family.fields,
    ...Object.fromEntries(["COMPLETENESS", "RELEVANCE", ...DETAIL_FIELDS[family.family]]
      .map((name) => [name, name.toLowerCase()])),
  };
  const detailFamily = { ...family, fields };
  const result = rowsToObjects(await execute({
    root: { alias: "e", entity: `ENTITY_TYPE_${family.entity}` },
    timeRange: { absolute: { start: new Date(eventTime - 1000).toISOString(), end: new Date(eventTime + 1000).toISOString() } },
    filter: and([
      pred(familyField(family, "EVENT_ID"), "OPERATOR_EQ", row.id),
      pred(familyField(family, "ENDPOINT_ID"), "OPERATOR_EQ", row.endpointId),
    ]),
    output: { dimensions: familyDimensions(detailFamily) }, limit: 1,
  }));
  const detail = result[0];
  if (!detail || detail.id !== row.id || detail.endpointId !== row.endpointId) {
    throw new Error("Selected activity is unavailable.");
  }
  return { ...row, data: familyRow(detail, detailFamily).data };
}
